import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { stopImpersonation } from '@/api/endpoints/organisationLifecycle';
import { useLifecycleError } from '@/components/backoffice/organisations/lifecycleError';

/**
 * SU-1 / ENG-10 / CEO-14: the persistent banner of « ouvrir en tant que ».
 * First in the banner slot (DES-14: impersonation > telephony > paused
 * session > CRM). Says whose organisation is shown, that it is read-only and
 * until when; « Quitter » ends it and goes back to the back office. When the
 * 30 minutes are over the server ignores the cookie: the page goes back too.
 */
export function useImpersonation() {
  return useAuthStore((s) => s.impersonation);
}

const BACK_OFFICE = '/AdminOrganisations';
/** After an expiry the server still reports (clock skew), check again a minute later. */
const RECHECK_MS = 60_000;

export function ImpersonationBanner() {
  const { t, i18n } = useTranslation(['admin']);
  const impersonation = useImpersonation();
  const organisationName = useAuthStore((s) => s.activeMembership?.organisationName);
  const memberEmail = useAuthStore((s) => s.user?.email);
  const [leaving, setLeaving] = useState(false);
  const { notify: showErrorToast } = useLifecycleError();

  // Expiry: back to the back office, as the super admin. REV-W7-F6: the
  // browser clock may be ahead of the server's (or the date unreadable), so
  // the page does not trust its own countdown: it ends the impersonation
  // (409 when already over), reads the session again and leaves only once
  // the server no longer reports one. Otherwise it checks again a minute
  // later, never in a tight loop.
  const attempts = useRef(0);
  useEffect(() => {
    if (!impersonation) return undefined;
    const ms = new Date(impersonation.expiresAt).getTime() - Date.now();
    const delay = attempts.current > 0 || !Number.isFinite(ms) ? RECHECK_MS : Math.max(0, ms) + 1000;
    // The callback is not cancelled once started: the refresh it awaits
    // re-renders (and may unmount) this banner before it decides.
    const timer = window.setTimeout(async () => {
      attempts.current += 1;
      try { await stopImpersonation(); } catch { /* already over (409) or offline: the refresh decides */ }
      try { await useAuthStore.getState().refresh(); } catch { /* refresh keeps its own error state */ }
      if (!useAuthStore.getState().impersonation) window.location.assign(BACK_OFFICE);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [impersonation]);

  if (!impersonation) return null;

  const leave = async () => {
    setLeaving(true);
    try {
      await stopImpersonation();
    } catch (err) {
      // Already over (409): leaving is still the right move.
      const code = (err as { code?: string } | null)?.code;
      if (code !== 'IMPERSONATION_NOT_ACTIVE') {
        setLeaving(false);
        showErrorToast(err);
        return;
      }
    }
    window.location.assign(BACK_OFFICE);
  };

  const expires = new Date(impersonation.expiresAt);
  const until = Number.isFinite(expires.getTime())
    ? expires.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    : '—';
  return (
    <div
      role="status"
      data-testid="impersonation-banner"
      className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <Eye className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        <span className="font-medium">{t('admin:impersonation.banner', { organisation: organisationName ?? '', member: memberEmail ?? '' })}</span>{' '}
        <span>{t('admin:impersonation.readOnly', { time: until })}</span>
      </p>
      <Button size="sm" variant="outline" className="min-h-11" onClick={() => void leave()} disabled={leaving}>
        {t('admin:impersonation.leave')}
      </Button>
    </div>
  );
}

export default ImpersonationBanner;
